import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { AlertTriangle, Info, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'

type CalloutTone = 'info' | 'warning' | 'safe'

const TONES: Record<CalloutTone, { surface: string; icon: string; fallback: LucideIcon }> = {
  info: { surface: 'bg-[var(--color-tint-blue)]', icon: 'text-[var(--color-info)]', fallback: Info },
  warning: { surface: 'bg-[var(--color-tint-orange)]', icon: 'text-[var(--color-warning)]', fallback: AlertTriangle },
  safe: { surface: 'bg-[var(--color-tint-green)]', icon: 'text-[var(--color-success)]', fallback: ShieldCheck },
}

export interface CalloutProps {
  title?: string
  children: ReactNode
  tone?: CalloutTone
  icon?: LucideIcon
  className?: string
}

/**
 * A sentence the page needs the reader to take in before the data.
 *
 * <p>Several screens explain a rule up front — "not opened is not zero", "nothing here deletes
 * anything". As a grey paragraph that reads as fine print and gets skipped; a tinted band with
 * an icon reads as the thing to know.
 */
export function Callout({ title, children, tone = 'info', icon, className }: CalloutProps) {
  const theme = TONES[tone]
  const Icon = icon ?? theme.fallback

  return (
    <div
      role="note"
      className={cn(
        'flex items-start gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] p-4 text-sm',
        theme.surface,
        className,
      )}
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--color-card)] shadow-sm">
        <Icon className={cn('size-4', theme.icon)} aria-hidden="true" />
      </span>
      <div className="min-w-0 pt-1 text-[var(--color-body)]">
        {title && <p className="mb-0.5 font-semibold text-[var(--color-heading)]">{title}</p>}
        {children}
      </div>
    </div>
  )
}
